import { useState, useEffect, useCallback, Fragment } from "react";
import { toast } from "react-toastify";
import { Loader2, Edit, Eye, ChevronRight, PlusCircle, Check, Download } from "lucide-react";
import QRCode from "qrcode";
import { useSuperAdmin } from "../../context/SuperAdminContext.jsx";
import { CampaignModal, AssetModal, StatusPill, ConfirmModal } from "./Campaignmodals.jsx";
import linkicon from "../../assets/link.png";
import {
  USE_API, DUMMY_CAMPAIGNS, EMPTY_FILTERS,
  normalizeCampaign, workersOf, fmtDate,
} from "./Campaignutils";
import Daterangepicker from "../Daterangepicker.jsx";
import Pagination from "../Common/Pagination.jsx";

const LIMIT = 10; // campaigns per page

// lower-case + trim
const st = (s) => (s || "").toLowerCase().trim();

// Not-published assets can be edited (pencil); published assets are view-only
const canEditAsset = (a) => !a.isPublished;

// ── Filters 
const FIELD_W = 220;
const FIELD_H = "32px";

const lbl = "block text-[11px] text-gray-500 mb-1 leading-none";
const inp =
  "w-full h-8 px-2 text-sm text-gray-700 border border-gray-300 rounded bg-white " +
  "placeholder:text-gray-400 hover:border-indigo-400 focus:outline-none focus:border-indigo-500";

function FilterField({ label, children }) {
  return (
    <div style={{ width: FIELD_W }} className="max-w-full">
      <label className={lbl}>{label}</label>
      {children}
    </div>
  );
}

function CampaignFilters({ onApply }) {
  const [f, setF] = useState(EMPTY_FILTERS);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  return (
    <div className="bg-white rounded-xl shadow-sm px-4 py-3 mb-3">
      <div className="flex flex-wrap items-end gap-x-3 gap-y-2">
        <FilterField label="Search">
          <input
            value={f.search}
            onChange={(e) => set("search")(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && onApply(f)}
            placeholder="Campaign name or code"
            className={inp}
          />
        </FilterField>

        <FilterField label="Status">
          <div className="relative">
            <select
              value={f.status}
              onChange={(e) => set("status")(e.target.value)}
              className={`${inp} appearance-none pr-7 cursor-pointer`}
            >
              <option>All</option>
              <option>Yet to start</option>
              <option>Active</option>
              <option>Paused</option>
              <option>Expired</option>
            </select>
            <svg
              className="w-4 h-4 text-gray-500 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </FilterField>

        <FilterField label="Start Date">
          <Daterangepicker
            startDate={f.startFrom}
            endDate={f.startTo}
            maxDate="2100-12-31"
            height={FIELD_H}
            onChange={({ startDate, endDate }) =>
              setF((p) => ({ ...p, startFrom: startDate, startTo: endDate }))
            }
          />
        </FilterField>

        <button
          onClick={() => onApply(f)}
          className="h-8 px-4 bg-indigo-600 text-white text-sm font-semibold rounded hover:bg-indigo-700"
        >
          Apply
        </button>
      </div>
    </div>
  );
}

// ── Summary 
const Item = ({ color, label, value }) => (
  <span className="flex items-center gap-1.5 text-xs text-gray-500">
    <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
    {label} : <b className="text-gray-800">{value}</b>
  </span>
);

const num = (n) => (n && typeof n === "object" ? Object.keys(n).length : n ?? 0);

function CampaignSummary({ totals, onAdd }) {
  const v = (n) => (totals ? num(n) : "-");
  return (
    <div className="bg-[#F2F6F5] rounded-xl px-4 py-3.5 mb-4 flex flex-wrap items-center justify-between gap-3">
      <div>
        <h3 className="text-base font-bold text-gray-900 mb-1.5">Campaign Summary</h3>
        <div className="flex flex-wrap gap-5">
          <Item color="#16A34A" label="Total Campaigns" value={v(totals?.campaigns)} />
          <Item color="#DB2777" label="Total Assets" value={v(totals?.assets)} />
          <Item color="#EA580C" label="Total Registered Workers" value={v(totals?.workers)} />
        </div>
      </div>
      <button onClick={onAdd} className="px-4 py-1 bg-indigo-600 text-white text-sm font-semibold rounded-md hover:bg-indigo-700">
        + Add Campaign
      </button>
    </div>
  );
}

function CountWithTip({ value, tip }) {
  return (
    <div
      tabIndex={tip ? 0 : undefined}
      className={`group relative inline-block font-semibold text-gray-800 ${tip ? "cursor-pointer" : ""}`}
    >
      {value}
      {tip && (
        <div className="hidden group-hover:block group-focus:block absolute top-full left-1/2 -translate-x-1/2 mt-1 z-20 bg-gray-900 text-white text-xs font-normal rounded-md px-2.5 py-1.5 whitespace-nowrap leading-relaxed text-left">
          {tip.map(([k, v]) => (
            <div key={k}>{k}: {v}</div>
          ))}
        </div>
      )}
    </div>
  );
}

// Campaign name with description shown on hover.
function CampaignName({ campaign: c, onEdit }) {
  const desc = c.description || c.desc || "";
  const dim = st(c.status) === "expired" || st(c.status) === "paused";

  return (
    <div className="flex items-center gap-1">
      <div
        tabIndex={desc ? 0 : undefined}
        className={`group relative ${desc ? "cursor-pointer" : ""}`}
      >
        <p className={`font-semibold ${dim ? "text-gray-400" : "text-gray-800"}`}>
          {c.name}
        </p>

        {desc && (
          <div
            className="
              invisible opacity-0
              group-hover:visible group-hover:opacity-100
              group-focus-within:visible group-focus-within:opacity-100
              absolute top-full left-0 mt-1
              w-52 max-w-xs
              z-[9999]
              rounded-md border border-gray-700
              bg-gray-900 text-white
              px-2 py-1.5
              text-[11px] font-normal leading-snug
              whitespace-normal break-words
              shadow-lg
              transition-opacity duration-150
              pointer-events-none
            "
          >
            {desc}
          </div>
        )}
      </div>

      <button
        onClick={() => onEdit(c)}
        className="shrink-0 rounded-lg p-1 hover:bg-blue-50"
        title={st(c.status) === "expired" ? "Edit end date" : "Edit Campaign"}
        aria-label="Edit Campaign"
      >
        <Edit size={13} color="#3B82F6" />
      </button>
    </div>
  );
}

// ── Expanded section (assets) 
const ASSET_ROW =
  "grid grid-cols-[1.6fr_1.3fr_0.9fr_0.9fr_1.5fr_1.3fr_0.7fr_1.2fr] items-center gap-3";

const ASSET_HEADERS = [
  "Asset Name", "Locations", "Channel", "Source",
  "Asset Code", "Created By", "Registered Workers", "Status",
];

// Plain value cell: truncates long text, full text on hover
const Cell = ({ children, title, className = "" }) => (
  <div className={`min-w-0 truncate text-[11px] text-gray-700 ${className}`} title={title}>
    {children}
  </div>
);

// Makes sure the link always carries &referrer=<asset code>
const withReferrer = (link, code) => {
  if (!link) return link;
  try {
    const u = new URL(link);
    if (!u.searchParams.has("referrer")) u.searchParams.set("referrer", code);
    return u.toString();
  } catch {
    // not an absolute URL: append manually
    if (/[?&]referrer=/.test(link)) return link;
    return `${link}${link.includes("?") ? "&" : "?"}referrer=${encodeURIComponent(code)}`;
  }
};

// Published / Not published switch. Once published it is locked (cannot be undone).
function StatusSwitch({ published, busy, onToggle }) {
  return (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        role="switch"
        aria-checked={published}
        aria-label={published ? "Published" : "Publish asset"}
        title={published ? "Published assets cannot be unpublished" : "Publish asset"}
        disabled={busy || published}
        onClick={onToggle}
        className={`relative w-8 h-[18px] rounded-full transition-colors shrink-0 ${
          published ? "bg-green-500" : "bg-gray-300"
        } ${busy ? "opacity-60 cursor-wait" : published ? "cursor-not-allowed" : "cursor-pointer"}`}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-[14px] h-[14px] rounded-full bg-white shadow transition-transform ${
            published ? "translate-x-3.5" : ""
          }`}
        />
      </button>
      <span className={`text-[11px] font-semibold whitespace-nowrap ${published ? "text-green-600" : "text-amber-600"}`}>
        {published ? "Published" : "Not published"}
      </span>
    </div>
  );
}

// Asset name hover tooltip
function DescTip({ desc, children }) {
  const [pos, setPos] = useState(null);

  const show = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const flip = r.bottom + 120 > window.innerHeight; // not enough room below → open upward
    setPos(
      flip
        ? { bottom: window.innerHeight - r.top + 4, left: r.left }
        : { top: r.bottom + 4, left: r.left }
    );
  };
  const hide = () => setPos(null);

  return (
    <div
      className="min-w-0"
      tabIndex={desc ? 0 : undefined}
      onMouseEnter={desc ? show : undefined}
      onMouseLeave={hide}
      onFocus={desc ? show : undefined}
      onBlur={hide}
    >
      {children}
      {desc && pos && (
        <div
          style={{ position: "fixed", ...pos }}
          className="z-[9999] w-52 max-w-xs rounded-md border border-gray-700 bg-gray-900 text-white px-2 py-1.5 text-[11px] font-normal leading-snug whitespace-normal break-words shadow-lg pointer-events-none"
        >
          {desc}
        </div>
      )}
    </div>
  );
}

// Copies the asset's UTM link to the clipboard
function CopyLinkButton({ link }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
      } else {
        const ta = document.createElement("textarea");
        ta.value = link;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      toast.success("Asset link copied", { toastId: "copy-link" });
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Failed to copy link");
    }
  };

  return (
    <button
      type="button"
      onClick={copy}
      className="shrink-0 p-0.5 rounded-md hover:bg-indigo-50 transition-colors"
      title="Copy asset link"
      aria-label="Copy asset link"
    >
      {copied ? (
        <Check size={15} color="#16A34A" />
      ) : (
        <img src={linkicon} alt="" className="w-[15px] h-[15px] object-contain" />
      )}
    </button>
  );
}

// Generates a QR code from the asset's link and downloads it as a PNG
function DownloadQrButton({ link, fileName }) {
  const [busy, setBusy] = useState(false);

  const download = async () => {
    try {
      setBusy(true);
      const dataUrl = await QRCode.toDataURL(link, {
        width: 1024, // high-res so it prints sharply
        margin: 2,
        errorCorrectionLevel: "M",
      });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `${String(fileName || "asset").replace(/[^\w-]+/g, "_")}-qr.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success("QR code downloaded", { toastId: "download-qr" });
    } catch {
      toast.error("Failed to generate QR code");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={download}
      disabled={busy}
      className="shrink-0 p-0.5 rounded-md hover:bg-indigo-50 transition-colors disabled:opacity-60"
      title="Download QR code"
      aria-label="Download QR code"
    >
      {busy ? (
        <Loader2 size={15} className="animate-spin text-gray-500" />
      ) : (
        <Download size={15} color="#4F46E5" />
      )}
    </button>
  );
}

function CampaignAssets({ campaign, assets = [], loading, error, toggling = {}, onRetry, onEditAsset, onToggleStatus }) {
  const campExpired = st(campaign.status) === "expired";

  return (
    <div className="bg-gray-50 px-4 py-3">
      {loading ? (
        <div className="flex items-center justify-center gap-2 py-4 text-xs text-gray-400">
          <Loader2 size={16} className="animate-spin" /> Loading assets...
        </div>
      ) : error ? (
        <p className="text-xs text-red-500 text-center py-3">
          Failed to load assets.{" "}
          <button onClick={() => onRetry(campaign)} className="font-semibold underline">Retry</button>
        </p>
      ) : assets.length === 0 ? (
        <p className="text-xs text-gray-400 text-center py-3">No assets for this campaign.</p>
      ) : (
        <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
          <div className="min-w-[900px]">
            {/* Column headers */}
            <div className={`${ASSET_ROW} px-3 py-1.5 bg-gray-100 border-b border-gray-200`}>
              {ASSET_HEADERS.map((h, i) => (
                <div
                  key={h}
                  className={`text-[10px] font-semibold uppercase tracking-wide text-gray-500 ${i === 0 ? "pl-3" : ""}`}
                >
                  {h}
                </div>
              ))}
            </div>

            {/* Rows */}
            <div className="divide-y divide-gray-100">
              {assets.map((a) => (
                <div key={a.id} className={`${ASSET_ROW} px-3 py-1.5 hover:bg-gray-50 transition-colors`}>
                  {/* Accent bar + name + edit/view */}
                  <div
                    className="pl-2.5 border-l-[3px] min-w-0"
                    style={{ borderColor: a.isPublished ? "#14B8A6" : "#D1D5DB" }}
                  >
                    <div className="flex items-center gap-1 min-w-0">
                      <DescTip desc={a.desc}>
                        <span
                          className={`block text-[12px] font-semibold text-gray-800 truncate ${a.desc ? "cursor-pointer" : ""}`}
                          title={a.desc ? undefined : a.name}
                        >
                          {a.name}
                        </span>
                      </DescTip>

                      {/* No edit / view button when the campaign is expired */}
                      {!campExpired && (
                        <button
                          onClick={() => onEditAsset(a, campaign)}
                          className="shrink-0 p-1 rounded-lg hover:bg-blue-50"
                          title={canEditAsset(a) ? "Edit Asset" : "View Asset"}
                          aria-label={canEditAsset(a) ? "Edit Asset" : "View Asset"}
                        >
                          {canEditAsset(a) ? (
                            <Edit size={13} color="#3B82F6" />
                          ) : (
                            <Eye size={13} color="#6B7280" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* All locations, comma separated (full list on hover) */}
                  <Cell title={a.location}>{a.location || "-"}</Cell>
                  <Cell title={a.channel}>{a.channel || "-"}</Cell>
                  <Cell title={a.source}>{a.source || "-"}</Cell>

                  {/* Code + copy + download */}
                  <div className="flex items-center gap-1 min-w-0">
                    <span className="font-mono text-[10px] text-gray-700 truncate" title={a.code}>
                      {a.code}
                    </span>
                    {a.utmLink && a.status=="published" && (
                      <span className="inline-flex items-center shrink-0">
                        <CopyLinkButton link={withReferrer(a.utmLink, a.code)} />
                        <DownloadQrButton link={withReferrer(a.utmLink, a.code)} fileName={a.code} />
                      </span>
                    )}
                  </div>

                  {/* Created by, date underneath */}
                  <div className="min-w-0 leading-tight">
                    <p className="text-[11px] text-gray-700 truncate" title={a.createdBy}>
                      {a.createdBy ?? "-"}
                    </p>
                    <p className="text-[10px] text-gray-400">{fmtDate(a.createdAt)}</p>
                  </div>

                  <Cell className="font-semibold text-gray-800">
                    {a.workers ?? a.worker_count ?? 0}
                  </Cell>

                  <div>
                    {campExpired ? (
                      <StatusPill status={a.status} />
                    ) : (
                      <StatusSwitch
                        published={a.isPublished}
                        busy={!!toggling[a.id]}
                        onToggle={() => onToggleStatus(a, campaign)}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Campaign table ───────────────────────────────────────────────────────────
const COLS = 9;

function CampaignTable({ campaigns, loading, expanded, assetsMap, toggling, onToggle, onRetryAssets, onToggleAssetStatus, onEditCampaign, onAddAsset, onEditAsset }) {
  return (
    <div className="max-h-[70vh] overflow-y-auto overflow-x-auto relative border border-gray-100 rounded-lg bg-white">
      <table className="w-full text-[13px] border-collapse">
        <thead className="sticky top-0 bg-gray-100 shadow-sm z-[50]">
          <tr className="text-left text-gray-900">
            <th className="px-3 py-2 w-8" />
            <th className="px-3 py-2 font-semibold">Campaign Name</th>
            <th className="px-3 py-2 font-semibold">Created By</th>
            <th className="px-3 py-2 font-semibold">Start Date</th>
            <th className="px-3 py-2 font-semibold">End Date</th>
            <th className="px-3 py-2 font-semibold text-center">Total Assets</th>
            <th className="px-3 py-2 font-semibold text-center">Registered Workers</th>
            <th className="px-3 py-2 font-semibold">Status</th>
            <th className="px-3 py-2 font-semibold text-center">Add Asset</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={COLS} className="py-10 text-center">
                <Loader2 size={22} className="animate-spin mx-auto mb-2 text-gray-400" />
                <p className="text-xs text-gray-400">Loading campaigns...</p>
              </td>
            </tr>
          ) : campaigns.length === 0 ? (
            <tr>
              <td colSpan={COLS} className="py-10 text-center text-sm text-gray-500 font-medium">
                No campaigns found.
              </td>
            </tr>
          ) : (
            campaigns.map((c) => {
              const open = !!expanded[c.id];
              const cnt = c.counts || { total: 0, published: 0, notPublished: 0 };
              const published = c.publishedAssets ?? cnt.published ?? 0;
              const notPublished = c.notPublishedAssets ?? cnt.notPublished ?? 0;
              const total = c.totalAssets ?? cnt.total ?? published + notPublished;
              const status = st(c.status);

              return (
                <Fragment key={c.id}>
                  <tr className="border-b border-gray-100 hover:bg-gray-50 transition-all">
                    <td className="px-3 py-1.5">
                      <button
                        onClick={() => onToggle(c)}
                        className="p-0.5 rounded hover:bg-gray-100"
                        aria-label={open ? "Collapse" : "Expand"}
                      >
                        <ChevronRight
                          size={15}
                          className={`text-gray-500 transition-transform ${open ? "rotate-90" : ""}`}
                        />
                      </button>
                    </td>

                    <td className="px-3 py-1 whitespace-nowrap leading-tight">
                      <CampaignName campaign={c} onEdit={onEditCampaign} />
                      <p className="font-mono text-[11px] text-gray-400">{c.code}</p>
                    </td>

                    <td className="px-3 py-1 whitespace-nowrap leading-tight">
                      <p>{c.createdBy ?? "-"}</p>
                      <p className="text-[11px] text-gray-400">{fmtDate(c.createdAt)}</p>
                    </td>

                    <td className="px-3 py-1 whitespace-nowrap">{fmtDate(c.startDate)}</td>
                    <td className="px-3 py-1 whitespace-nowrap">{fmtDate(c.endDate)}</td>

                    <td className="px-3 py-1 text-center">
                      <CountWithTip
                        value={total}
                        tip={[
                          ["Published", published],
                          ["Not published", notPublished],
                        ]}
                      />
                    </td>

                    <td className="px-3 py-1 text-center font-semibold text-gray-800">{workersOf(c)}</td>
                    <td className="px-3 py-1 whitespace-nowrap"><StatusPill status={c.status} /></td>

                    {/* Add asset: allowed for yet to start / active, warning for paused, hidden for expired */}
                    <td className="px-3 py-1 whitespace-nowrap text-center">
                      {status !== "expired" ? (
                        <button
                          onClick={() => onAddAsset(c)}
                          className="p-1 rounded-lg hover:bg-indigo-50"
                          title="Add Asset"
                          aria-label="Add Asset"
                        >
                          <PlusCircle size={16} color={status === "paused" ? "#9CA3AF" : "#4F46E5"} />
                        </button>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  </tr>

                  {open && (
                    <tr>
                      <td colSpan={COLS} className="p-0 border-b border-gray-100">
                        <CampaignAssets
                          campaign={c}
                          assets={USE_API ? assetsMap[c.id]?.list : c.assets}
                          loading={USE_API && (assetsMap[c.id]?.loading ?? true)}
                          error={USE_API && assetsMap[c.id]?.error}
                          toggling={toggling}
                          onRetry={onRetryAssets}
                          onEditAsset={onEditAsset}
                          onToggleStatus={onToggleAssetStatus}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

// GET /assets returns raw rows:
// { asset_id, campaign_id, name, channel, source, status: "Published" | "Not published",
//   created_by, created_at, utmLink, locations: [{ _id, name }], worker_count }
const normalizeAsset = (a) => {
  const locs = Array.isArray(a.locations) ? a.locations : [];
  const by = a.created_by ?? a.createdBy ?? a.creator;
  const status = st(a.status); // "published" | "not published" | "expired"
  return {
    id: a._id || a.id || a.asset_id,
    code: a.asset_id || a.code || a.asset_code,
    name: a.name,
    status,
    isPublished: status === "published",
    channel: a.channel ?? a.asset_channel,
    source: a.source ?? a.asset_source,
    locationIds: locs.map((l) => l._id || l.id),
    location: locs.map((l) => l.name).join(", "), // ALL locations
    createdBy: by && typeof by === "object" ? by.name || by.full_name : by,
    createdAt: a.created_at ?? a.createdAt,
    workers: a.worker_count ?? a.workers ?? 0,
    desc: a.desc ?? a.description ?? "",
    utmLink: a.utmLink ?? a.utm_link ?? "",
  };
};

// ── Main Component ───────────────────────────────────────────────────────────
const TODAY = () => new Date().toISOString().slice(0, 10);
const ME = "You";
// total_workers may arrive as a number or as a { workerId: name } map
const countOf = (v) => (v && typeof v === "object" ? Object.keys(v).length : Number(v) || 0);

export default function ManageCampaigns() {
  const { fetchcampaigns, fetchActiveLocations, fetchAssets, fetchAssetById, toggle_asset_status } = useSuperAdmin();

  const [campaigns, setCampaigns] = useState([]);
  const [summary, setSummary] = useState(null); // { campaigns, assets, workers }
  const [pagination, setPagination] = useState({ page: 1, limit: LIMIT, total: 0, total_pages: 1 });
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState(EMPTY_FILTERS); // applied filters only
  const [loading, setLoading] = useState(true);

  const [locationOptions, setLocationOptions] = useState([]);
  const [locLoading, setLocLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [assetsMap, setAssetsMap] = useState({}); // { [campaignId]: { loading, error, list } }
  const [toggling, setToggling] = useState({});   // { [assetId]: true } while a publish call runs

  // modal state: { mode, initial?, preset? } | null
  const [campaignModal, setCampaignModal] = useState(null);
  const [assetModal, setAssetModal] = useState(null);
  // info modal (e.g. campaign must be activated first)
  const [notice, setNotice] = useState(null);
  // confirm modal before publishing an asset: { asset, campaign } | null
  const [publishConfirm, setPublishConfirm] = useState(null);

  // Campaigns
  const loadCampaigns = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);

      if (!USE_API) {
        const list = DUMMY_CAMPAIGNS.map(normalizeCampaign);
        setCampaigns((prev) => (prev.length ? prev : list));
        setPagination({ page: 1, limit: LIMIT, total: list.length, total_pages: 1 });
        setSummary({
          campaigns: list.length,
          assets: list.reduce((s, c) => s + (c.counts?.total ?? 0), 0),
          workers: list.reduce((s, c) => s + countOf(workersOf(c)), 0),
        });
        return;
      }

      const params = { page, limit: LIMIT };
      if (filters.search.trim()) params.search = filters.search.trim();
      if (filters.status !== "All") params.status = filters.status; // e.g. "yet to start"
      if (filters.startFrom) params.start_from = filters.startFrom;
      if (filters.startTo) params.start_to = filters.startTo;

      const res = await fetchcampaigns(params);
      const body = res?.data;
      if (body?.success && Array.isArray(body?.campaigns)) {
        setCampaigns(body.campaigns.map(normalizeCampaign));
        setPagination(body.pagination || { page, limit: LIMIT, total: body.campaigns.length, total_pages: 1 });
        setSummary({
          campaigns: body.summary?.total_campaigns ?? 0,
          assets: body.summary?.total_assets ?? 0,
          workers: body.summary?.total_workers ?? 0,
        });
      } else {
        toast.error(body?.message || "Failed to load campaigns");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load campaigns");
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  // reload whenever the page or the applied filters change
  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  // Assets of one campaign: GET /assets?campaign_id=<campaign code>
  const loadAssets = useCallback(async (campaign) => {
    setAssetsMap((p) => ({ ...p, [campaign.id]: { ...p[campaign.id], loading: true, error: false } }));
    try {
      const res = await fetchAssets({ campaign_id: campaign.code });
      const d = res?.data;
      const rows = [d?.assets, d?.data, d].find(Array.isArray) || [];
      setAssetsMap((p) => ({
        ...p,
        [campaign.id]: { loading: false, error: false, list: rows.map(normalizeAsset) },
      }));
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load assets");
      setAssetsMap((p) => ({ ...p, [campaign.id]: { loading: false, error: true, list: [] } }));
    }
  }, [fetchAssets]);

  // Arrow click: expand/collapse; load the asset list every time a campaign is opened
  const toggleCampaign = (campaign) => {
    const willOpen = !expanded[campaign.id];
    setExpanded((p) => ({ ...p, [campaign.id]: willOpen }));
    if (willOpen && USE_API) loadAssets(campaign);
  };

  const applyFilters = (f) => {
    setFilters({ ...f });
    setPage(1);
  };

  // Locations: fetched only when a campaign modal opens (cached after first success)
  const loadLocations = useCallback(async () => {
    if (!USE_API) {
      setLocationOptions([]);
      return;
    }
    if (locationOptions.length) return;
    try {
      setLocLoading(true);
      const res = await fetchActiveLocations();
      setLocationOptions(res?.locations || []);
    } catch (err) {
      console.error("Location fetch failed:", err);
      toast.error("Failed to load locations");
    } finally {
      setLocLoading(false);
    }
  }, [locationOptions.length]);

  const openCampaignModal = (mode, initial) => {
    setCampaignModal({ mode, initial });
    loadLocations();
  };

  // Add asset:
  //  - yet to start / active → open the modal
  //  - paused                → warning: activate the campaign first
  //  - expired               → not allowed
  const handleAddAsset = (campaign) => {
    const s = st(campaign.status);
    if (s === "expired") return;
    if (s === "paused") {
      setNotice({
        title: "Campaign is paused",
        message: "This campaign is paused. Please activate the campaign first, then add an asset.",
      });
      return;
    }
    setAssetModal({ mode: "add", preset: campaign });
  };

  // Edit / view asset: load the full asset (GET /assets/:asset_id) so channel, source and
  // locations are prefilled; fall back to the row data if the request fails.
  // Not-published assets open for editing, published assets open view-only (see AssetModal).
  const openAssetEdit = async (asset, campaign) => {
    if (st(campaign.status) === "expired") return;

    let initial = { ...asset, campaignId: asset.campaignId ?? campaign.id };

    if (USE_API) {
      try {
        const res = await fetchAssetById(asset.code);
        const full = res?.data?.asset || res?.data?.data || {};
        initial = {
          ...initial,
          name: full.name ?? asset.name,
          channel: full.channel ?? asset.channel ?? "",
          source: full.source ?? asset.source ?? "",
          locationIds: Array.isArray(full.locations)
            ? full.locations.map((l) => l._id || l.id)
            : asset.locationIds,
          desc: full.desc ?? asset.desc ?? "",
        };
      } catch (err) {
        console.error("Asset fetch failed:", err);
      }
    }

    setAssetModal({ mode: "edit", initial, preset: campaign });
  };

  // Publish switch. Publishing is one-way: a published asset cannot be set back.
  const toggleAssetStatus = (asset, campaign) => {
    const s = st(campaign.status);
    if (asset.isPublished || s === "expired") return;

    if (s === "paused") {
      setNotice({
        title: "Campaign is paused",
        message: "This campaign is paused. Please activate the campaign first, then publish this asset.",
      });
      return;
    }
    setPublishConfirm({ asset, campaign });
  };

  const publishAsset = async () => {
    const { asset, campaign } = publishConfirm;

    if (!USE_API) {
      setCampaigns((prev) =>
        prev.map((c) => {
          if (c.id !== campaign.id) return c;
          const assets = (c.assets || []).map((a) =>
            a.id === asset.id ? { ...a, isPublished: true, status: "published" } : a
          );
          const published = assets.filter((a) => a.isPublished).length;
          return {
            ...c,
            assets,
            counts: { ...c.counts, published, notPublished: assets.length - published },
            publishedAssets: published,
            notPublishedAssets: assets.length - published,
          };
        })
      );
      setPublishConfirm(null);
      return;
    }

    setToggling((p) => ({ ...p, [asset.id]: true }));
    try {
      const res = await toggle_asset_status(asset.code, "Published");
      if (res?.data?.success) {
        toast.success("Asset published");
        setAssetsMap((p) => {
          const cur = p[campaign.id];
          if (!cur) return p;
          return {
            ...p,
            [campaign.id]: {
              ...cur,
              list: cur.list.map((a) =>
                a.id === asset.id ? { ...a, status: "published", isPublished: true } : a
              ),
            },
          };
        });
        loadCampaigns(true); // refresh published / not published counts
      } else {
        toast.error(res?.data?.message || "Failed to publish");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to publish");
    } finally {
      setToggling((p) => {
        const { [asset.id]: _done, ...rest } = p;
        return rest;
      });
      setPublishConfirm(null);
    }
  };

  // ── Dummy-mode local upserts (API mode simply reloads) ──
  const saveCampaign = async (data) => {
    if (USE_API) {
      await loadCampaigns();
      campaigns.filter((c) => expanded[c.id]).forEach((c) => loadAssets(c));
      return;
    }
    setCampaigns((prev) =>
      data.id
        ? prev.map((c) => {
            if (c.id !== data.id) return c;
            const merged = { ...c, ...data, updatedBy: ME, updatedAt: TODAY() };
            // expired campaigns stay expired; others toggle between active / paused
            if (st(c.status) !== "expired") merged.status = data.isActive ? "active" : "paused";
            return merged;
          })
        : [
            {
              ...data,
              id: String(Date.now()),
              status: data.isActive ? "active" : "paused",
              assets: [],
              counts: { total: 0, published: 0, notPublished: 0 },
              createdBy: ME,
              createdAt: TODAY(),
              updatedBy: "",
              updatedAt: "",
            },
            ...prev,
          ]
    );
  };

  const saveAsset = (data) => {
    if (USE_API) {
      // keep the campaign open so the new / updated asset is visible after reload
      const camp = assetModal?.preset;
      if (camp) {
        setExpanded((p) => ({ ...p, [camp.id]: true }));
        loadAssets(camp);
      }
      return loadCampaigns(true); // refreshes the counts quietly
    }
    setCampaigns((prev) =>
      prev.map((c) => {
        if (c.id !== data.campaignId) return c;
        const assets = data.id
          ? c.assets.map((a) => (a.id === data.id ? { ...a, ...data, updatedBy: ME, updatedAt: TODAY() } : a))
          : [...c.assets, { ...data, id: String(Date.now()), workers: 0, createdBy: ME, createdAt: TODAY() }];
        const published = assets.filter((a) => a.isPublished).length;
        return {
          ...c,
          assets,
          counts: { ...c.counts, total: assets.length, published, notPublished: assets.length - published },
        };
      })
    );
    setExpanded((p) => ({ ...p, [data.campaignId]: true }));
  };

  return (
    <div className="p-6 mt-12 bg-gray-50 min-h-full" style={{ fontFamily: "'Segoe UI', sans-serif" }}>
      <h2 className="text-xl font-bold text-gray-900 text-center mb-4">Campaigns / Sourcing Assets</h2>

      <CampaignFilters onApply={applyFilters} />
      <CampaignSummary totals={loading && !summary ? null : summary} onAdd={() => openCampaignModal("add")} />

      <CampaignTable
        campaigns={campaigns}
        loading={loading}
        expanded={expanded}
        assetsMap={assetsMap}
        toggling={toggling}
        onToggle={toggleCampaign}
        onRetryAssets={loadAssets}
        onToggleAssetStatus={toggleAssetStatus}
        onEditCampaign={(c) => openCampaignModal("edit", c)}
        onAddAsset={handleAddAsset}
        onEditAsset={openAssetEdit}
      />

      {!loading && pagination.total > 0 && (
        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.total_pages}
          onPageChange={setPage}
          itemsPerPage={pagination.limit}
          totalItems={pagination.total}
          activeColor="#4F46E5"
          textColor="#4F46E5"
          containerStyle={{ padding: "10px 0", backgroundColor: "transparent" }}
        />
      )}

      {campaignModal && (
        <CampaignModal
          mode={campaignModal.mode}
          initial={campaignModal.initial}
          campaigns={campaigns}
          locationOptions={locationOptions}
          locationsLoading={locLoading}
          onClose={() => setCampaignModal(null)}
          onSave={saveCampaign}
        />
      )}
      {assetModal && (
        <AssetModal
          mode={assetModal.mode}
          initial={assetModal.initial}
          presetCampaign={assetModal.preset}
          campaigns={campaigns}
          onClose={() => setAssetModal(null)}
          onSave={saveAsset}
        />
      )}

      {notice && (
        <ConfirmModal
          title={notice.title}
          message={notice.message}
          onClose={() => setNotice(null)}
        />
      )}

      {publishConfirm && (
        <ConfirmModal
          title="Publish asset?"
          message="Once published, this asset cannot be edited or set back to not published. Do you want to continue?"
          confirmLabel="Yes, Publish"
          busy={!!toggling[publishConfirm.asset.id]}
          onConfirm={publishAsset}
          onClose={() => setPublishConfirm(null)}
        />
      )}
    </div>
  );
}